export const ORG_TYPES = [
  'pub_or_bar',
  'sports_club',
  'charity',
  'creator',
  'brand',
  'other',
] as const

type OrgType = (typeof ORG_TYPES)[number]

export const ORG_TYPE_LABELS: Record<OrgType, string> = {
  pub_or_bar: 'Pub or Bar',
  sports_club: 'Sports Club',
  charity: 'Charity',
  creator: 'Content Creator / Community',
  brand: 'Brand',
  other: 'Other',
}

export type AffiliateStatus = 'PENDING' | 'APPROVED' | 'REJECTED' | 'SUSPENDED'

type AttributionType = 'LIFETIME' | 'TIME_LIMITED'

type AttributionScope = 'PLATFORM_WIDE'

interface IAffiliateContact {
  full_name: string
  email: string
  dob: string | null
}

export interface IAffiliate {
  id: number
  organisation_name: string
  org_type: OrgType
  city: string
  website: string | null
  notes: string | null
  logo_url: string | null
  status: AffiliateStatus
  commission_rate: number
  attribution_type: AttributionType
  attribution_days: number | null
  attribution_scope: AttributionScope
  referral_slug: string
  referral_url: string
  players_signed_up: number
  current_balance_pence: number
  pending_withdrawal_pence: number
  lifetime_earnings_pence: number
  member_since: string
  partner_terms_accepted_at: string | null
  partner_terms_version: number | null
  contact: IAffiliateContact
}

export interface IAffiliateApplyPayload {
  organisation_name: string
  org_type: OrgType
  city: string
  website?: string
  notes?: string
  dob?: string
  accepted_terms: true
}

export interface IAffiliateApplyResponse {
  affiliate_id: number
  status: AffiliateStatus
}

export type IAffiliateUpdatePayload = Partial<
  Pick<IAffiliate, 'organisation_name' | 'org_type' | 'city' | 'website' | 'notes'>
>

export interface IAffiliateWithdrawPayload {
  amount_pence: number
  account_holder_name: string
  sort_code: string
  account_number: string
}

export interface IAffiliateWithdrawResponse {
  withdrawal_request_id: number
  status: string
  amount_pence: number
}

export interface IAffiliateRefLookup {
  organisation_name: string
  logo_url: string | null
  commission_rate: number
}

export type AffiliateLinkTargetType = 'draw' | 'game'

export interface IAffiliateLinkTarget {
  id: number
  type: AffiliateLinkTargetType
  title: string
  prize_name: string
  image_url: string | null
  ticket_price_pence: number | null
  starts_at: string | null
  closes_at: string | null
}

type AffiliateLedgerTransactionType =
  | 'CREDIT'
  | 'REFUND_CLAWBACK'
  | 'MANUAL_ADJUSTMENT'
  | 'WITHDRAWAL_HOLD'
  | 'WITHDRAWAL_RELEASE'

export interface IAffiliateLedgerEntry {
  id: number
  transaction_type: AffiliateLedgerTransactionType
  amount_pence: number
  raffle_id: number | null
  cash_amount_spent_pence: number | null
  created: string
}

export const AFFILIATE_ERRORS = {
  AFFILIATE_NOT_FOUND: 'AFFILIATE_NOT_FOUND',
  AFFILIATE_ALREADY_EXISTS: 'AFFILIATE_ALREADY_EXISTS',
  AFFILIATE_DOB_REQUIRED: 'AFFILIATE_DOB_REQUIRED',
  AFFILIATE_UNDERAGE: 'AFFILIATE_UNDERAGE',
  AFFILIATE_WITHDRAWAL_NOT_ALLOWED: 'AFFILIATE_WITHDRAWAL_NOT_ALLOWED',
  AFFILIATE_INSUFFICIENT_BALANCE: 'AFFILIATE_INSUFFICIENT_BALANCE',
  AFFILIATE_ACTIVE_WITHDRAWAL_EXISTS: 'AFFILIATE_ACTIVE_WITHDRAWAL_EXISTS',
  AFFILIATE_WITHDRAWAL_BELOW_MINIMUM: 'AFFILIATE_WITHDRAWAL_BELOW_MINIMUM',
  PARTNER_TERMS_UNAVAILABLE: 'PARTNER_TERMS_UNAVAILABLE',
  PARTNER_TERMS_OUTDATED: 'PARTNER_TERMS_OUTDATED',
} as const

export type AffiliateErrorCode = (typeof AFFILIATE_ERRORS)[keyof typeof AFFILIATE_ERRORS]

export const AFFILIATE_ERROR_MESSAGES: Partial<Record<AffiliateErrorCode, string>> = {
  AFFILIATE_ALREADY_EXISTS: "You've already applied",
  AFFILIATE_DOB_REQUIRED: 'Please enter your date of birth',
  AFFILIATE_UNDERAGE: 'Partners must be 18 or over',
  AFFILIATE_WITHDRAWAL_NOT_ALLOWED: 'Withdrawals are not available for this account',
  AFFILIATE_INSUFFICIENT_BALANCE: 'Amount is over your current balance',
  AFFILIATE_ACTIVE_WITHDRAWAL_EXISTS: 'You have a withdrawal in progress',
  AFFILIATE_WITHDRAWAL_BELOW_MINIMUM: 'Amount is below the minimum withdrawal',
  PARTNER_TERMS_UNAVAILABLE: "Partner applications aren't open yet. Please try again later.",
  PARTNER_TERMS_OUTDATED: 'Our Partner Terms have changed. Please review them and apply again.',
}

export const AFFILIATE_REF_STORAGE_KEY = 'tbl_affiliate_ref'
export const AFFILIATE_REF_COOKIE_DAYS = 30
export const AFFILIATE_REF_TTL_SECONDS = 60 * 60 * 24 * AFFILIATE_REF_COOKIE_DAYS
export const DEFAULT_COMMISSION_RATE = 0.1
